import { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { CaretUp, CaretDown } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import api from '../../../api/api'
import { getRefetchRecentReviews } from '../../../reducers/reputationManagementReducer'

import Rating from './Rating'
import Platforms from './Platforms'
import {
  stats as statsArray,
  defaultRating,
  defaultOverviewAnalytics,
  getPeriodTime,
} from './helpers'

const Overview = ({ buildingId, period, refetchRecentReviews }) => {
  const { t } = useTranslation(['organisation'])
  const [analytics, setAnalytics] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const fetchAnalytics = async () => {
    try {
      const { from, to } = getPeriodTime(period)
      setIsLoading(true)
      const { data = [] } = await api.getReputationAnalytics(buildingId, from, to)
      setAnalytics(data)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const totalReviewsCount = useMemo(() => {
    if (!analytics || !analytics.generalRating) return 0
    return analytics.generalRating.stats.reduce((acc, s) => acc + s.reviewCount, 0)
  }, [analytics])

  const rating = useMemo(() => {
    if (
      !analytics ||
      !analytics.generalRating ||
      Object.keys(analytics.generalRating).length === 0
    ) {
      return defaultRating
    }

    const rating = analytics.generalRating
    const stats = statsArray.map((r) => {
      const stat = rating.stats.find((s) => s.rating === r)
      if (!stat) return { rating: r, reviewCount: 0, percentage: 0 }

      const percentage = (stat.reviewCount / totalReviewsCount) * 100
      return {
        ...stat,
        rating: r,
        percentage,
      }
    })

    return {
      ...rating,
      stats,
    }
  }, [analytics, totalReviewsCount])

  const providerRating = useMemo(() => {
    if (!analytics || !analytics.providerRating) return []
    return analytics.providerRating
  }, [analytics])

  const overviewAnalytics = useMemo(() => {
    if (!analytics || !analytics.analytics || Object.keys(analytics.analytics).length === 0) {
      return defaultOverviewAnalytics
    }
    return analytics.analytics
  }, [analytics])

  useEffect(() => {
    fetchAnalytics()
  }, [buildingId, period])

  useEffect(() => {
    if (refetchRecentReviews) {
      fetchAnalytics()
    }
  }, [refetchRecentReviews, fetchAnalytics])

  return (
    <div className={`reputation-dashboard-tab__overview ${isLoading ? 'is-loading' : ''}`}>
      <Rating rating={rating} />
      <div className="vertical-divider" />
      <Platforms providerRating={providerRating} />
      <div className="vertical-divider" />
      <div className="reputation-dashboard-tab__analytics">
        <h4 className="reputation-dashboard-tab__overview-title">
          {t('organisation:reputationManagement.overview.analytics')}
        </h4>
        <div className="analytics">
          <div className="analytics__col">
            <div className="analytics__item">
              <h4 className="analytics__item--text">
                {t('organisation:reputationManagement.overview.reviewsReceived')}
              </h4>
              <h2 className="analytics__item--title">
                {overviewAnalytics.reviewCount}
                {overviewAnalytics.reviewCountDiff !== 0 && (
                  <span
                    className={overviewAnalytics.reviewCountDiff >= 0 ? 'increase' : 'decrease'}
                  >
                    {overviewAnalytics.reviewCountDiff >= 0 ? (
                      <CaretUp size={12} weight="fill" />
                    ) : (
                      <CaretDown size={12} weight="fill" />
                    )}
                    {Math.abs(overviewAnalytics.reviewCountDiff)}%
                  </span>
                )}
              </h2>
            </div>
            <div className="analytics__item">
              <h4 className="analytics__item--text">
                {t('organisation:reputationManagement.overview.threeStarOrLess')}
              </h4>
              <h2 className="analytics__item--title">
                {overviewAnalytics.lowRated}
                {overviewAnalytics.lowRatedDiff !== 0 && (
                  <span className={overviewAnalytics.lowRatedDiff >= 0 ? 'increase' : 'decrease'}>
                    {overviewAnalytics.lowRatedDiff >= 0 ? (
                      <CaretUp size={12} weight="fill" />
                    ) : (
                      <CaretDown size={12} weight="fill" />
                    )}
                    {Math.abs(overviewAnalytics.lowRatedDiff)}%
                  </span>
                )}
              </h2>
            </div>
          </div>
          <div className="analytics__col">
            <div className="analytics__item">
              <h4 className="analytics__item--text">
                {t('organisation:reputationManagement.overview.avrTimeToReply')}
              </h4>
              <h2 className="analytics__item--title">
                {overviewAnalytics.avgReplyTime || 0}
                <span>{t('organisation:reputationManagement.overview.days')}</span>
                {overviewAnalytics.avgReplyTimeDiff !== 0 && (
                  <span
                    className={overviewAnalytics.avgReplyTimeDiff >= 0 ? 'increase' : 'decrease'}
                  >
                    {overviewAnalytics.avgReplyTimeDiff >= 0 ? (
                      <CaretUp size={12} weight="fill" />
                    ) : (
                      <CaretDown size={12} weight="fill" />
                    )}
                    {Math.abs(overviewAnalytics.avgReplyTimeDiff)}%
                  </span>
                )}
              </h2>
            </div>
            <div className="analytics__item">
              <h4 className="analytics__item--text">
                {t('organisation:reputationManagement.overview.fourStarsOrMore')}
              </h4>
              <h2 className="analytics__item--title">
                {overviewAnalytics.highRated}
                {overviewAnalytics.highRatedDiff !== 0 && (
                  <span className={overviewAnalytics.highRatedDiff >= 0 ? 'increase' : 'decrease'}>
                    {overviewAnalytics.highRatedDiff >= 0 ? (
                      <CaretUp size={12} weight="fill" />
                    ) : (
                      <CaretDown size={12} weight="fill" />
                    )}
                    {Math.abs(overviewAnalytics.highRatedDiff)}%
                  </span>
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Overview.propTypes = {
  buildingId: PropTypes.number.isRequired,
  period: PropTypes.object,
  refetchRecentReviews: PropTypes.bool,
}

export default connect(
  (state) => ({
    refetchRecentReviews: getRefetchRecentReviews(state),
  }),
  null,
)(Overview)
